import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Grid, Paper, Button, useTheme, Checkbox, Pagination, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControl, FormControlLabel, InputLabel, Select, MenuItem, Chip, Stack, Accordion, AccordionSummary, AccordionDetails, ToggleButton, ToggleButtonGroup, SelectChangeEvent, Badge, CircularProgress, InputAdornment, Divider } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckIcon from '@mui/icons-material/Check';
import AddIcon from '@mui/icons-material/Add';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import SortAscendingIcon from '@mui/icons-material/Sort';
import SortDescendingIcon from '@mui/icons-material/Sort';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SortIcon from '@mui/icons-material/Sort';
import CloseIcon from '@mui/icons-material/Close';
import Popover from '@mui/material/Popover';
import { adminEndpoints } from '../services/api';
import { useError } from '../contexts/ErrorContext';
import ImportCardsFromJson from '../components/ImportCardsFromJson';

interface Card {
  id: string;
  word: string;
  forbiddenWords: string[];
  difficulty: 'easy' | 'medium' | 'hard';
  category1: string;
  category2: string;
  status: 'active' | 'inactive';
  referenceId?: string;
  createdAt: string;
  updatedAt: string;
}

interface Package {
  id: string;
  cards: Card[];
  description: string;
  createdAt: string;
  updatedAt: string;
}

const PackageDetail: React.FC = () => {
  const { packageId } = useParams<{ packageId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const { showError } = useError();
  const [packageData, setPackageData] = useState<Package | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const itemsPerPage = 20;
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInWord, setSearchInWord] = useState(true);
  const [searchInForbidden, setSearchInForbidden] = useState(true);
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [sortBy, setSortBy] = useState<string>('none');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  
  // Add state for edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingCard, setEditingCard] = useState<Card | null>(null);
  const [editFormData, setEditFormData] = useState<Partial<Card>>({});
  const [newForbiddenWord, setNewForbiddenWord] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [sortAnchorEl, setSortAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [savingCard, setSavingCard] = useState(false);

  // Reference card popover state
  const [referenceAnchorEl, setReferenceAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [referenceCard, setReferenceCard] = useState<Card | null>(null);
  const [referenceLoading, setReferenceLoading] = useState(false);
  const [referenceError, setReferenceError] = useState<string | null>(null);

  // Refs for input fields
  const wordInputRef = useRef<HTMLInputElement>(null);
  const forbiddenWordInputRef = useRef<HTMLInputElement>(null);
  const category1InputRef = useRef<HTMLInputElement>(null);
  const category2InputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const fetchPackageData = async () => {
      try {
        const response = await adminEndpoints.getPackageDetails(packageId!);
        if (response.data.packages && response.data.packages.length > 0) {
          setPackageData(response.data.packages[0]);
        }
      } catch (error) {
        console.error('Error fetching package data:', error);
        showError('Failed to fetch package details');
      } finally {
        setLoading(false);
      }
    };

    if (packageId) {
      fetchPackageData();
    }
  }, [packageId, showError]);

  // Focus word input when dialog opens
  useEffect(() => {
    if (editDialogOpen && wordInputRef.current) {
      setTimeout(() => {
        wordInputRef.current?.focus();
      }, 100);
    }
  }, [editDialogOpen]);

  // Global keyboard handler for Shift+Enter when dialog is open
  useEffect(() => {
    if (!editDialogOpen || savingCard) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const isEnter = e.key === 'Enter' || e.keyCode === 13;
      if (isEnter && e.shiftKey) {
        // Check if focus is on one of our input fields
        const activeElement = document.activeElement;
        const isInDialog = activeElement?.closest('[role="dialog"]');
        
        if (isInDialog && editFormData.forbiddenWords && editFormData.forbiddenWords.length === 5) {
          e.preventDefault();
          e.stopPropagation();
          handleSave();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [editDialogOpen, savingCard, editFormData.forbiddenWords]);

  const handleBack = () => {
    navigate(-1);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const handleStatusChange = async (cardId: string, newStatus: 'active' | 'inactive') => {
    try {
      const response = await adminEndpoints.updateCard(packageId!, cardId, {
        status: newStatus
      });
      
      if (response.data.success) {
        // Update the local state
        setPackageData(prevData => {
          if (!prevData) return null;
          return {
            ...prevData,
            cards: prevData.cards.map(card => 
              card.id === cardId 
                ? { ...card, status: newStatus }
                : card
            )
          };
        });
      }
    } catch (error) {
      console.error('Error updating card status:', error);
      showError('Failed to update card status');
    }
  };

  const handleDeleteCard = async (cardId: string) => {
    if (!window.confirm('Are you sure you want to delete this card?')) {
      return;
    }

    try {
      const response = await adminEndpoints.deleteCard(packageId!, cardId);
      
      if (response.data.success) {
        // Update the local state by removing the deleted card
        setPackageData(prevData => {
          if (!prevData) return null;
          return {
            ...prevData,
            cards: prevData.cards.filter(card => card.id !== cardId)
          };
        });
      }
    } catch (error) {
      console.error('Error deleting card:', error);
      showError('Failed to delete card');
    }
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCreateClick = () => {
    setIsCreating(true);
    setEditingCard(null);
    setEditFormData({
      word: '',
      forbiddenWords: [],
      difficulty: 'medium',
      category1: '',
      category2: '',
      status: 'active'
    });
    setEditDialogOpen(true);
  };

  const handleEditClick = (card: Card) => {
    setIsCreating(false);
    setEditingCard(card);
    setEditFormData({
      word: card.word.charAt(0).toUpperCase() + card.word.slice(1),
      forbiddenWords: [...card.forbiddenWords],
      difficulty: card.difficulty,
      category1: card.category1.charAt(0).toUpperCase() + card.category1.slice(1),
      category2: card.category2 ? card.category2.charAt(0).toUpperCase() + card.category2.slice(1) : '',
      status: card.status?.toLowerCase() === 'inactive' ? 'active' : card.status,
    });
    setEditDialogOpen(true);
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
    setEditingCard(null);
    setEditFormData({});
    setNewForbiddenWord('');
    setIsCreating(false);
    handleCloseReference();
  };

  const capitalizeWords = (str: string): string => {
    return str
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const handleAddForbiddenWord = () => {
    if (newForbiddenWord.trim() && editFormData.forbiddenWords) {
      // Capitalize first letter of every word
      const capitalizedWord = capitalizeWords(newForbiddenWord.trim());
      
      // Check if we already have 5 words
      if (editFormData.forbiddenWords.length >= 5) {
        showError('Maximum 5 forbidden words allowed');
        return;
      }
      
      const newForbiddenWords = [...editFormData.forbiddenWords, capitalizedWord];
      const willHave5Words = newForbiddenWords.length >= 5;
      
      setEditFormData({
        ...editFormData,
        forbiddenWords: newForbiddenWords
      });
      setNewForbiddenWord('');
      
      // If we now have 5 words, focus on Category 1 field
      if (willHave5Words) {
        setTimeout(() => {
          category1InputRef.current?.focus();
        }, 100);
      }
    }
  };

  const handleRemoveForbiddenWord = (wordToRemove: string) => {
    if (editFormData.forbiddenWords) {
      setEditFormData({
        ...editFormData,
        forbiddenWords: editFormData.forbiddenWords.filter(word => word !== wordToRemove)
      });
    }
  };

  const handleSave = async () => {
    if (!editFormData || !editFormData.word) return;

    // Validate forbidden words count
    if (!editFormData.forbiddenWords || editFormData.forbiddenWords.length !== 5) {
      showError('Exactly 5 forbidden words are required');
      return;
    }

    // Ensure word and all forbidden words have every word capitalized
    const word = editFormData.word;
    const capitalizedWord = capitalizeWords(word);
    const validatedForbiddenWords = editFormData.forbiddenWords.map(word => 
      capitalizeWords(word)
    );
    
    // Capitalize categories
    const category1 = editFormData.category1 ? 
      capitalizeWords(editFormData.category1) : '';
    const category2 = editFormData.category2 ? 
      capitalizeWords(editFormData.category2) : '';

    const cardData = {
      ...editFormData,
      word: capitalizedWord,
      forbiddenWords: validatedForbiddenWords,
      category1,
      category2
    };

    setSavingCard(true);
    try {
      if (isCreating) {
        const response = await adminEndpoints.createCard(packageId!, cardData);
        if (response.data.success) {
          // Refresh package data
          const updatedResponse = await adminEndpoints.getPackageDetails(packageId!);
          if (updatedResponse.data.packages && updatedResponse.data.packages.length > 0) {
            setPackageData(updatedResponse.data.packages[0]);
          }
          // Reset form for next card creation (don't close dialog)
          setEditFormData({
            word: '',
            forbiddenWords: [],
            difficulty: 'medium',
            category1: '',
            category2: '',
            status: 'active'
          });
          setNewForbiddenWord('');
          // Focus word input for next card
          setTimeout(() => {
            wordInputRef.current?.focus();
          }, 100);
        }
      } else if (editingCard) {
        const response = await adminEndpoints.updateCard(packageId!, editingCard.id, cardData);
        if (response.data.success) {
          // Update the local state
          setPackageData(prevData => {
            if (!prevData) return null;
            return {
              ...prevData,
              cards: prevData.cards.map(card => 
                card.id === editingCard.id 
                  ? { ...card, ...cardData }
                  : card
              )
            };
          });
          handleEditClose();
        }
      }
    } catch (error) {
      console.error('Error saving card:', error);
      showError(`Failed to ${isCreating ? 'create' : 'update'} card`);
    } finally {
      setSavingCard(false);
    }
  };

  const handleImportSuccess = () => {
    setImportDialogOpen(false);
    // Refresh package data
    const fetchPackageData = async () => {
      try {
        const response = await adminEndpoints.getPackageDetails(packageId!);
        if (response.data.packages && response.data.packages.length > 0) {
          setPackageData(response.data.packages[0]);
        }
      } catch (error) {
        console.error('Error fetching package data:', error);
        showError('Failed to fetch package details');
      }
    };
    fetchPackageData();
  };

  const getDifficultyCounts = () => {
    if (!packageData?.cards) return { easy: 0, medium: 0, hard: 0 };
    return packageData.cards.reduce((acc, card) => {
      acc[card.difficulty]++;
      return acc;
    }, { easy: 0, medium: 0, hard: 0 });
  };

  const getCategoryCounts = () => {
    if (!packageData?.cards) return {};
    const counts: { [key: string]: number } = {};
    packageData.cards.forEach(card => {
      counts[card.category1] = (counts[card.category1] || 0) + 1;
      if (card.category2) {
        counts[card.category2] = (counts[card.category2] || 0) + 1;
      }
    });
    return counts;
  };

  const handleStatusFilterChange = (
    event: React.MouseEvent<HTMLElement>,
    newFilter: 'all' | 'active' | 'inactive',
  ) => {
    if (newFilter !== null) {
      setStatusFilter(newFilter);
      setPage(1); // Reset to first page when filter changes
    }
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setPage(1); // Reset to first page when search changes
  };

  const handleFilterClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleSearchScopeAllChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setSearchInWord(checked);
    setSearchInForbidden(checked);
    setPage(1);
  };

  const handleSearchScopeWordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    if (!checked && !searchInForbidden) return; // keep at least one scope selected
    setSearchInWord(checked);
    setPage(1);
  };

  const handleSearchScopeForbiddenChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    if (!checked && !searchInWord) return; // keep at least one scope selected
    setSearchInForbidden(checked);
    setPage(1);
  };

  const handleSortClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setSortAnchorEl(event.currentTarget);
  };

  const handleSortClose = () => {
    setSortAnchorEl(null);
  };

  const handleSortChange = (value: string) => {
    setSortBy(value);
    setPage(1);
    handleSortClose();
  };

  const handleShowReference = async (event: React.MouseEvent<HTMLButtonElement>) => {
    const referenceId = editingCard?.referenceId;
    if (!referenceId) return;

    setReferenceAnchorEl(event.currentTarget);
    setReferenceLoading(true);
    setReferenceError(null);
    setReferenceCard(null);

    try {
      const response = await adminEndpoints.getCardById(referenceId);
      setReferenceCard(response.data.card);
    } catch (error) {
      console.error('Error fetching reference card:', error);
      setReferenceError('Failed to fetch reference card');
    } finally {
      setReferenceLoading(false);
    }
  };

  const handleCloseReference = () => {
    setReferenceAnchorEl(null);
    setReferenceCard(null);
    setReferenceError(null);
  };

  const handleExportToJson = () => {
    if (!packageData) return;

    const exportData = {
      packageId: packageData.id,
      description: packageData.description,
      cards: packageData.cards.map(card => ({
        word: card.word,
        forbiddenWords: card.forbiddenWords,
        difficulty: card.difficulty,
        category1: card.category1,
        category2: card.category2,
        referenceId: card.referenceId ?? '',
        status: card.status
      }))
    };

    const dataStr = JSON.stringify(exportData, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `package-${packageData.id}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  if (loading) {
    return <Typography>Loading...</Typography>;
  }

  if (!packageData) {
    return <Typography>Package not found</Typography>;
  }

  // Calculate pagination with filter and sort
  const normalizedSearchQuery = searchQuery.trim().toLowerCase();
  const filteredCards = packageData?.cards?.filter(card => {
    const matchesStatus = statusFilter === 'all' || card.status === statusFilter;
    if (!matchesStatus) return false;
    if (!normalizedSearchQuery) return true;
    return (
      (searchInWord && card.word.toLowerCase().includes(normalizedSearchQuery)) ||
      (searchInForbidden && card.forbiddenWords.some(word => word.toLowerCase().includes(normalizedSearchQuery)))
    );
  }) || [];

  const sortedCards = [...filteredCards].sort((a, b) => {
    if (sortBy === 'none') return 0;
    
    const [field, direction] = sortBy.split('-');
    
    if (field === 'word') {
      return direction === 'asc' 
        ? a.word.localeCompare(b.word)
        : b.word.localeCompare(a.word);
    }
    
    const dateA = new Date(field === 'created' ? a.createdAt : a.updatedAt);
    const dateB = new Date(field === 'created' ? b.createdAt : b.updatedAt);
    return direction === 'asc' 
      ? dateA.getTime() - dateB.getTime()
      : dateB.getTime() - dateA.getTime();
  });

  const totalPages = Math.ceil(sortedCards.length / itemsPerPage);
  const startIndex = (page - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentCards = sortedCards.slice(startIndex, endIndex);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' }, 
        gap: 2,
        mb: 3 
      }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
        >
          Back
        </Button>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'row',
          gap: 1,
          width: { xs: '100%', sm: 'auto' },
          flexWrap: 'wrap'
        }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<FileDownloadIcon />}
            onClick={handleExportToJson}
            sx={{ 
              justifyContent: 'flex-start',
              textTransform: 'none',
              fontSize: { xs: '0.875rem', sm: '0.875rem' },
              whiteSpace: 'nowrap'
            }}
          >
            Export
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<FileUploadIcon />}
            onClick={() => setImportDialogOpen(true)}
            sx={{ 
              justifyContent: 'flex-start',
              textTransform: 'none',
              fontSize: { xs: '0.875rem', sm: '0.875rem' },
              whiteSpace: 'nowrap'
            }}
          >
            Import
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleCreateClick}
            sx={{ 
              justifyContent: 'flex-start',
              textTransform: 'none',
              fontSize: { xs: '0.875rem', sm: '0.875rem' },
              whiteSpace: 'nowrap'
            }}
          >
            Create
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
          {packageData.id}
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Description: {packageData.description || 'No description available'}
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Created: {formatDate(packageData.createdAt)}
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Last Updated: {formatDate(packageData.updatedAt)}
        </Typography>
        <Box sx={{ 
          display: 'flex', 
          gap: 2, 
          alignItems: 'center',
          mt: 2,
          mb: 2,
          p: 2,
          backgroundColor: 'rgba(0, 0, 0, 0.02)',
          borderRadius: 1
        }}>
          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            Total Cards: {packageData.cards?.length || 0}
          </Typography>
          <Typography variant="body1" color="success.main" sx={{ fontWeight: 'bold' }}>
            Active: {packageData.cards?.filter(card => card.status === 'active').length || 0}
          </Typography>
          <Typography variant="body1" color="error.main" sx={{ fontWeight: 'bold' }}>
            Inactive: {packageData.cards?.filter(card => card.status === 'inactive').length || 0}
          </Typography>
        </Box>

        <Accordion sx={{ mb: 1 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1">Difficulty Distribution</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {Object.entries(getDifficultyCounts()).map(([difficulty, count], index) => (
                <Box 
                  key={difficulty} 
                  sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    p: 1,
                    backgroundColor: index % 2 === 0 ? 'rgba(0, 0, 0, 0.02)' : 'transparent',
                    borderRadius: 1
                  }}
                >
                  <Typography variant="body2" sx={{ textTransform: 'capitalize', fontSize: '0.875rem' }}>
                    {difficulty}:
                  </Typography>
                  <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.875rem' }}>
                    {count}
                  </Typography>
                </Box>
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1">Category Distribution</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {Object.entries(getCategoryCounts())
                .sort((a, b) => b[1] - a[1])
                .map(([category, count], index) => (
                  <Box 
                    key={category} 
                    sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      p: 1,
                      backgroundColor: index % 2 === 0 ? 'rgba(0, 0, 0, 0.02)' : 'transparent',
                      borderRadius: 1
                    }}
                  >
                    <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                      {category}:
                    </Typography>
                    <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.875rem' }}>
                      {count}
                    </Typography>
                  </Box>
                ))}
            </Box>
          </AccordionDetails>
        </Accordion>
      </Paper>

      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        mb: 3,
        justifyContent: 'flex-end'
      }}>
        <Badge
          color="primary"
          variant="dot"
          invisible={statusFilter === 'all' && !normalizedSearchQuery && searchInWord && searchInForbidden}
        >
          <IconButton
            onClick={handleFilterClick}
            sx={{
              width: 40,
              height: 40,
              backgroundColor: 'action.hover',
              '&:hover': {
                backgroundColor: 'action.selected'
              }
            }}
          >
            <FilterAltIcon />
          </IconButton>
        </Badge>
        <Popover
          open={Boolean(filterAnchorEl)}
          anchorEl={filterAnchorEl}
          onClose={handleFilterClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <Box sx={{ p: 2, width: 320 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Status
            </Typography>
            <ToggleButtonGroup
              value={statusFilter}
              exclusive
              onChange={handleStatusFilterChange}
              size="small"
              fullWidth
              sx={{ mb: 2 }}
            >
              <ToggleButton value="all" sx={{ px: 2, py: 1 }}>
                All
              </ToggleButton>
              <ToggleButton value="active" sx={{ px: 2, py: 1, color: 'success.main' }}>
                Active
              </ToggleButton>
              <ToggleButton value="inactive" sx={{ px: 2, py: 1, color: 'error.main' }}>
                Inactive
              </ToggleButton>
            </ToggleButtonGroup>

            <Divider sx={{ mb: 2 }} />

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Search
            </Typography>
            <TextField
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder="Search cards..."
              size="small"
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => { setSearchQuery(''); setPage(1); }}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', mt: 0.5 }}>
              <FormControlLabel
                label="All"
                control={
                  <Checkbox
                    size="small"
                    checked={searchInWord && searchInForbidden}
                    indeterminate={searchInWord !== searchInForbidden}
                    onChange={handleSearchScopeAllChange}
                  />
                }
              />
              <FormControlLabel
                label="Main word"
                control={
                  <Checkbox
                    size="small"
                    checked={searchInWord}
                    onChange={handleSearchScopeWordChange}
                  />
                }
              />
              <FormControlLabel
                label="Forbidden words"
                control={
                  <Checkbox
                    size="small"
                    checked={searchInForbidden}
                    onChange={handleSearchScopeForbiddenChange}
                  />
                }
              />
            </Box>
          </Box>
        </Popover>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Badge
            color="primary"
            variant="dot"
            invisible={sortBy === 'none'}
          >
            <IconButton
              onClick={handleSortClick}
              sx={{
                width: 40,
                height: 40,
                backgroundColor: 'action.hover',
                '&:hover': {
                  backgroundColor: 'action.selected'
                }
              }}
            >
              <SortIcon />
            </IconButton>
          </Badge>
          <Popover
            open={Boolean(sortAnchorEl)}
            anchorEl={sortAnchorEl}
            onClose={handleSortClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
          >
            <Box sx={{ p: 1 }}>
              <MenuItem 
                onClick={() => handleSortChange('none')}
                selected={sortBy === 'none'}
                sx={{ fontSize: '0.75rem', py: 0.5 }}
              >
                None
              </MenuItem>
              <MenuItem 
                onClick={() => handleSortChange('created-asc')}
                selected={sortBy === 'created-asc'}
                sx={{ fontSize: '0.75rem', py: 0.5 }}
              >
                Created (Oldest First)
              </MenuItem>
              <MenuItem 
                onClick={() => handleSortChange('created-desc')}
                selected={sortBy === 'created-desc'}
                sx={{ fontSize: '0.75rem', py: 0.5 }}
              >
                Created (Newest First)
              </MenuItem>
              <MenuItem 
                onClick={() => handleSortChange('updated-asc')}
                selected={sortBy === 'updated-asc'}
                sx={{ fontSize: '0.75rem', py: 0.5 }}
              >
                Updated (Oldest First)
              </MenuItem>
              <MenuItem 
                onClick={() => handleSortChange('updated-desc')}
                selected={sortBy === 'updated-desc'}
                sx={{ fontSize: '0.75rem', py: 0.5 }}
              >
                Updated (Newest First)
              </MenuItem>
              <MenuItem 
                onClick={() => handleSortChange('word-asc')}
                selected={sortBy === 'word-asc'}
                sx={{ fontSize: '0.75rem', py: 0.5 }}
              >
                Word (A-Z)
              </MenuItem>
              <MenuItem 
                onClick={() => handleSortChange('word-desc')}
                selected={sortBy === 'word-desc'}
                sx={{ fontSize: '0.75rem', py: 0.5 }}
              >
                Word (Z-A)
              </MenuItem>
            </Box>
          </Popover>
        </Box>
      </Box>

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
          <Pagination 
            count={totalPages} 
            page={page} 
            onChange={handlePageChange}
            color="primary"
            size="large"
            siblingCount={1}
            boundaryCount={1}
          />
        </Box>
      )}

      <Grid container spacing={3}>
        {currentCards?.map((card) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={card.id}>
            <Paper
              sx={{
                p: 2,
                height: '100%',
                backgroundColor: card.status === 'active' 
                  ? 'rgba(76, 175, 80, 0.1)' 
                  : 'rgba(244, 67, 54, 0.1)',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center'
              }}
            >
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                gap: 1,
                mt: 0.5,
                mb: 2,
                p: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                width: '100%'
              }}>
                <Typography variant="h5" sx={{ mt: 0, mb: 0, fontWeight: 700, fontSize: '2rem' }}>
                  {card.word}
                </Typography>
                {card.forbiddenWords?.map((word, index) => (
                  <Typography 
                    key={index} 
                    variant="body1" 
                    color="text.secondary"
                    sx={{ fontWeight: 600 }}
                  >
                    {word}
                  </Typography>
                ))}
              </Box>
              <Box sx={{ 
                mt: 'auto',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0.5
              }}>
                <Typography variant="caption" color="text.secondary">
                  Category: {card.category1}
                  {card.category2 && `, ${card.category2}`}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Difficulty: {card.difficulty}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                  Created: {formatDate(card.createdAt)}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                  Updated: {formatDate(card.updatedAt)}
                </Typography>
                <Box sx={{ 
                  display: 'flex', 
                  gap: 3,
                  alignItems: 'center',
                  mt: 1
                }}>
                  <IconButton 
                    size="small" 
                    onClick={() => handleDeleteCard(card.id)}
                    sx={{ 
                      p: 0.5,
                      border: '1px solid',
                      borderColor: 'error.main',
                      '& .MuiSvgIcon-root': {
                        fontSize: 28,
                        color: 'error.main'
                      }
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                  <IconButton 
                    size="small" 
                    onClick={() => handleEditClick(card)}
                    sx={{ 
                      p: 0.5,
                      border: '1px solid',
                      borderColor: 'primary.main',
                      '& .MuiSvgIcon-root': {
                        fontSize: 28,
                        color: 'primary.main'
                      }
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton 
                    size="small"
                    onClick={() => handleStatusChange(card.id, card.status === 'active' ? 'inactive' : 'active')}
                    sx={{ 
                      p: 0.5,
                      border: '1px solid',
                      borderColor: card.status === 'active' ? 'success.main' : 'action.disabled',
                      '& .MuiSvgIcon-root': {
                        fontSize: 28,
                        color: card.status === 'active' ? 'success.main' : 'action.disabled'
                      }
                    }}
                  >
                    <CheckIcon />
                  </IconButton>
                </Box>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination 
            count={totalPages} 
            page={page} 
            onChange={handlePageChange}
            color="primary"
            size="large"
            siblingCount={1}
            boundaryCount={1}
          />
        </Box>
      )}

      {/* Edit/Create Dialog */}
      <Dialog open={editDialogOpen} onClose={handleEditClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography variant="h6" component="span">
              {isCreating ? 'Create New Card' : 'Edit Card'}
            </Typography>
            {!isCreating && editingCard?.referenceId && (
              <Button
                size="small"
                variant="outlined"
                onClick={handleShowReference}
                disabled={savingCard}
              >
                Referans
              </Button>
            )}
          </Box>
          <IconButton
            aria-label="close"
            onClick={handleEditClose}
            disabled={savingCard}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <Popover
          open={Boolean(referenceAnchorEl)}
          anchorEl={referenceAnchorEl}
          onClose={handleCloseReference}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          <Box sx={{ width: 280, p: 2, position: 'relative' }}>
            <IconButton
              aria-label="close-reference"
              size="small"
              onClick={handleCloseReference}
              sx={{ position: 'absolute', right: 4, top: 4 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
            {referenceLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={28} />
              </Box>
            )}
            {!referenceLoading && referenceError && (
              <Typography variant="body2" color="error" sx={{ pr: 3 }}>
                {referenceError}
              </Typography>
            )}
            {!referenceLoading && !referenceError && referenceCard && (
              <Box sx={{ pr: 3 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  {referenceCard.word}
                </Typography>
                <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                  {referenceCard.forbiddenWords?.map((word, index) => (
                    <Chip key={index} label={word} size="small" variant="outlined" />
                  ))}
                </Stack>
                <Typography variant="caption" color="text.secondary" display="block">
                  Category: {referenceCard.category1}
                  {referenceCard.category2 && `, ${referenceCard.category2}`}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  Difficulty: {referenceCard.difficulty}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  Status: {referenceCard.status}
                </Typography>
              </Box>
            )}
          </Box>
        </Popover>
        <DialogContent sx={{ position: 'relative' }}>
          {savingCard && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                zIndex: 1000,
                borderRadius: 1,
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <CircularProgress size={60} />
                <Typography variant="body1" color="text.secondary">
                  {isCreating ? 'Creating card...' : 'Saving changes...'}
                </Typography>
              </Box>
            </Box>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              inputRef={wordInputRef}
              label="Word"
              value={editFormData.word || ''}
              onChange={(e) => {
                const value = e.target.value;
                const capitalizedValue = capitalizeWords(value);
                setEditFormData({ ...editFormData, word: capitalizedValue });
              }}
              onKeyDown={(e) => {
                if (!savingCard) {
                  const isEnter = e.key === 'Enter' || e.keyCode === 13;
                  if (isEnter && e.shiftKey) {
                    // Shift+Enter triggers save
                    e.preventDefault();
                    e.stopPropagation();
                    handleSave();
                    return;
                  } else if (isEnter && !e.shiftKey) {
                    // Regular Enter moves to next field
                    e.preventDefault();
                    forbiddenWordInputRef.current?.focus();
                  }
                }
              }}
              onKeyPress={(e) => {
                if (!savingCard) {
                  const isEnter = e.key === 'Enter' || e.charCode === 13;
                  if (isEnter && e.shiftKey) {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSave();
                  }
                }
              }}
              disabled={savingCard}
              fullWidth
            />
            
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Forbidden Words ({editFormData.forbiddenWords?.length || 0}/5)
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap', gap: 1 }}>
                {editFormData.forbiddenWords?.map((word, index) => (
                  <Chip 
                    key={index} 
                    label={word} 
                    onDelete={savingCard ? undefined : () => handleRemoveForbiddenWord(word)}
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Stack>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  inputRef={forbiddenWordInputRef}
                  label="Add Forbidden Word"
                  value={newForbiddenWord}
                  onChange={(e) => {
                    const value = e.target.value;
                    const capitalizedValue = capitalizeWords(value);
                    setNewForbiddenWord(capitalizedValue);
                  }}
                  onKeyDown={(e) => {
                    if (!savingCard) {
                      const isEnter = e.key === 'Enter' || e.keyCode === 13;
                      if (isEnter && e.shiftKey) {
                        // Shift+Enter triggers save
                        e.preventDefault();
                        e.stopPropagation();
                        handleSave();
                        return;
                      } else if (isEnter && !e.shiftKey) {
                        // Regular Enter just adds word
                        e.preventDefault();
                        const forbiddenWordsCount = editFormData.forbiddenWords?.length || 0;
                        if (newForbiddenWord.trim() && forbiddenWordsCount < 5) {
                          // Add word (handleAddForbiddenWord will handle focus)
                          handleAddForbiddenWord();
                        }
                      }
                    }
                  }}
                  onKeyPress={(e) => {
                    if (!savingCard) {
                      const isEnter = e.key === 'Enter' || e.charCode === 13;
                      if (isEnter && e.shiftKey) {
                        e.preventDefault();
                        e.stopPropagation();
                        handleSave();
                      }
                    }
                  }}
                  fullWidth
                  size="small"
                  disabled={savingCard || (editFormData.forbiddenWords?.length || 0) >= 5}
                  helperText={(editFormData.forbiddenWords?.length || 0) >= 5 ? "Maximum 5 words reached" : ""}
                />
                <Button 
                  variant="contained" 
                  onClick={handleAddForbiddenWord}
                  disabled={savingCard || !newForbiddenWord.trim() || (editFormData.forbiddenWords?.length || 0) >= 5}
                >
                  Add
                </Button>
              </Box>
            </Box>
            
            <FormControl fullWidth>
              <InputLabel>Difficulty</InputLabel>
              <Select
                value={editFormData.difficulty || ''}
                label="Difficulty"
                onChange={(e) => setEditFormData({ ...editFormData, difficulty: e.target.value as 'easy' | 'medium' | 'hard' })}
                disabled={savingCard}
              >
                <MenuItem value="easy">Easy</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="hard">Hard</MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              inputRef={category1InputRef}
              label="Category 1"
              value={editFormData.category1 || ''}
              onChange={(e) => {
                const value = e.target.value;
                const capitalizedValue = capitalizeWords(value);
                setEditFormData({ ...editFormData, category1: capitalizedValue });
              }}
              onKeyDown={(e) => {
                if (!savingCard) {
                  const isEnter = e.key === 'Enter' || e.keyCode === 13;
                  if (isEnter && e.shiftKey) {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSave();
                  }
                }
              }}
              onKeyPress={(e) => {
                if (!savingCard) {
                  const isEnter = e.key === 'Enter' || e.charCode === 13;
                  if (isEnter && e.shiftKey) {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSave();
                  }
                }
              }}
              disabled={savingCard}
              fullWidth
            />
            
            <TextField
              inputRef={category2InputRef}
              label="Category 2"
              value={editFormData.category2 || ''}
              onChange={(e) => {
                const value = e.target.value;
                const capitalizedValue = capitalizeWords(value);
                setEditFormData({ ...editFormData, category2: capitalizedValue });
              }}
              onKeyDown={(e) => {
                if (!savingCard) {
                  const isEnter = e.key === 'Enter' || e.keyCode === 13;
                  if (isEnter && e.shiftKey) {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSave();
                  }
                }
              }}
              onKeyPress={(e) => {
                if (!savingCard) {
                  const isEnter = e.key === 'Enter' || e.charCode === 13;
                  if (isEnter && e.shiftKey) {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSave();
                  }
                }
              }}
              disabled={savingCard}
              fullWidth
            />
            
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={editFormData.status || ''}
                label="Status"
                onChange={(e) => setEditFormData({ ...editFormData, status: e.target.value as 'active' | 'inactive' })}
                disabled={savingCard}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditClose} disabled={savingCard}>Cancel</Button>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
            <Button 
              onClick={handleSave} 
              variant="contained" 
              color="primary"
              disabled={savingCard || !editFormData.forbiddenWords || editFormData.forbiddenWords.length !== 5}
              startIcon={savingCard ? <CircularProgress size={20} sx={{ color: 'white' }} /> : null}
            >
              {savingCard ? (isCreating ? 'Creating...' : 'Saving...') : (isCreating ? 'Create' : 'Save Changes')}
            </Button>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
              Shift+Enter
            </Typography>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onClose={() => setImportDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Import Cards From JSON</DialogTitle>
        <DialogContent>
          <ImportCardsFromJson 
            packageId={packageId!} 
            onSuccess={handleImportSuccess}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PackageDetail; 